import React, { useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import ScheduledChangesIcon from "../assets/images/timetable_rgb_red.svg";
import "../assets/styles/scheduledChanges.scss";
import ScheduleChangeUpdateForms from "../components/ScheduledChanges/Update/ScheduleChangeUpdateForms";
import ScheduleChangeUpdateButton from "../components/ScheduledChanges/Update/ScheduleChangeUpdateButton";
import {
  getScheduledChangeDetails,
  updateScheduledChange,
} from "../services/scheduledChangesApi";

const toDateTimeLocalValue = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const toApiDateTimeLocal = (value) => {
  if (!value) return null;
  return value.length === 16 ? `${value}:00` : value;
};

const parseLocalDateValue = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const pickFirstDefined = (obj, keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== null && value !== undefined && value !== "") {
      return value;
    }
  }
  return null;
};

const mapRecordToFormValues = (record) => {
  const scheduledRaw = pickFirstDefined(record, [
    "scheduledTime",
    "ScheduledTime",
    "executeAtLocal",
    "ExecuteAtLocal",
  ]);

  const rollbackRaw = pickFirstDefined(record, [
    "rollbackScheduledTime",
    "RollbackScheduledTime",
    "withdrawAtLocal",
    "WithdrawAtLocal",
  ]);

  return {
    scheduledTime: toDateTimeLocalValue(scheduledRaw),
    rollbackScheduledTime: toDateTimeLocalValue(rollbackRaw),
  };
};

const UpdateScheduledChange = () => {
  const { id } = useParams();
  const [record, setRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());
  const [initialValues, setInitialValues] = useState({
    scheduledTime: "",
    rollbackScheduledTime: "",
  });
  const [formValues, setFormValues] = useState({
    scheduledTime: "",
    rollbackScheduledTime: "",
  });

  useEffect(() => {
    if (!id) return;

    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      setIsLoading(true);
      try {
        const response = await getScheduledChangeDetails(id, controller.signal);

        if (cancelled) return;

        const payload = response?.data;
        const found = payload?.data ?? payload ?? null;

        setRecord(found ?? null);

        const nextValues = mapRecordToFormValues(found);
        setInitialValues(nextValues);
        setFormValues(nextValues);
      } catch {
        if (!cancelled) {
          setRecord(null);
          const emptyValues = { scheduledTime: "", rollbackScheduledTime: "" };
          setInitialValues(emptyValues);
          setFormValues(emptyValues);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [id]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNowTick(Date.now());
    }, 30000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const isDirty = useMemo(
    () =>
      formValues.scheduledTime !== initialValues.scheduledTime ||
      formValues.rollbackScheduledTime !== initialValues.rollbackScheduledTime,
    [formValues, initialValues]
  );

  const validationErrors = useMemo(() => {
    const errors = {
      scheduledTime: "",
      rollbackScheduledTime: "",
    };

    const now = new Date(nowTick);
    const scheduledDate = parseLocalDateValue(formValues.scheduledTime);
    const rollbackDate = parseLocalDateValue(formValues.rollbackScheduledTime);

    if (scheduledDate && scheduledDate < now) {
      errors.scheduledTime = "Data zaplanowania nie może być wcześniejsza niż obecna data i godzina.";
    }

    if (scheduledDate && rollbackDate && rollbackDate < scheduledDate) {
      errors.rollbackScheduledTime = "Data wycofania nie może być wcześniejsza niż data zaplanowania.";
    }

    return errors;
  }, [formValues, nowTick]);

  const hasValidationErrors = Boolean(
    validationErrors.scheduledTime || validationErrors.rollbackScheduledTime
  );


  const handleFormChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleReset = () => {
    setFormValues(initialValues);
  };

  const handleSave = async () => {
    if (!id || isSaving) return;

    if (hasValidationErrors) {
      alert("Popraw błędy walidacji formularza przed zapisem.");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        executeAtLocal: toApiDateTimeLocal(formValues.scheduledTime),
        withdrawAtLocal: toApiDateTimeLocal(formValues.rollbackScheduledTime),
      };

      await updateScheduledChange(id, payload);

      setInitialValues(formValues);
      setRecord((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          scheduledTime: payload.executeAtLocal,
          rollbackScheduledTime: payload.withdrawAtLocal,
        };
      });
      alert("Zmiany zostały zapisane.");
    } catch (error) {
      if (error?.response?.status === 423) {
        alert("Baza danych jest obecnie zablokowana. Zmiana nie może zostać wykonana");
      } else {
        alert("Nie udało się zapisać zmian.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="users-container">
      <Container fluid>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <img
              src={ScheduledChangesIcon}
              alt="Scheduled Changes Icon"
              style={{ width: "30px", height: "30px" }}
            />
          </Col>
          <Col>
            <h3 style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
              Edycja rekordu
            </h3>
          </Col>
        </Row>
        <Row className="align-items-center mb-4"></Row>
        <Row>
          <Col md={8}>
            <ScheduleChangeUpdateForms
              values={formValues}
              isLoading={isLoading}
              onChange={handleFormChange}
              validationErrors={validationErrors}
            />

            <div className="mt-3">
              <ScheduleChangeUpdateButton
                onReset={handleReset}
                onSave={handleSave}
                disabled={isLoading || !record || !isDirty || hasValidationErrors}
                isSaving={isSaving}
              />
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default UpdateScheduledChange;
